import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Search, Check, X, Eye, EyeOff, Edit2, RefreshCw, Shield, Mail, Save, Clock, Server, Database, AlertTriangle, CheckCircle, XCircle, FileText, Settings } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

import { productTypesAPI, standardsAPI } from '../../services/api';

export function DropdownManagerPage() {
  const [productTypes, setProductTypes] = useState([]);
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'productType' or 'standard'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, stdsRes] = await Promise.all([
        productTypesAPI.list(),
        standardsAPI.list()
      ]);
      setProductTypes(typesRes.data || []);
      setStandards(stdsRes.data || []);
    } catch (error) {
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = (type) => {
    setDialogType(type);
    setEditingItem(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (type, item) => {
    setDialogType(type);
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const apiModule = dialogType === 'productType' ? productTypesAPI : standardsAPI;
      if (editingItem) {
        await apiModule.update(editingItem.id, formData);
      } else {
        await apiModule.create(formData);
      }
      
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    
    try {
      const apiModule = type === 'productType' ? productTypesAPI : standardsAPI;
      await apiModule.delete(item.id);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
          Master Data Management
        </h1>
        <p className="text-muted-foreground">Manage dropdown values for Part Types and Standards</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Part Types */}
        <Card className="card-dashboard">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading uppercase">Part Types</CardTitle>
              <CardDescription>Used in Part Master dropdown</CardDescription>
            </div>
            <Button onClick={() => openAddDialog('productType')} size="sm" data-testid="add-part-type-btn">
              + Add Type
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No part types defined. Click "Add Type" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  productTypes.map((type) => (
                    <TableRow key={type.id} className="table-row">
                      <TableCell className="font-medium">{type.name}</TableCell>
                      <TableCell className="text-muted-foreground">{type.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditDialog('productType', type)}
                            data-testid={`edit-type-${type.id}`}
                          >
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete('productType', type)}
                            data-testid={`delete-type-${type.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Standards */}
        <Card className="card-dashboard">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading uppercase">Standards</CardTitle>
              <CardDescription>Used in Part Master dropdown (e.g., DIN, IS)</CardDescription>
            </div>
            <Button onClick={() => openAddDialog('standard')} size="sm" data-testid="add-standard-btn">
              + Add Standard
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {standards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No standards defined. Click "Add Standard" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  standards.map((std) => (
                    <TableRow key={std.id} className="table-row">
                      <TableCell className="font-medium">{std.name}</TableCell>
                      <TableCell className="text-muted-foreground">{std.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditDialog('standard', std)}
                            data-testid={`edit-std-${std.id}`}
                          >
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete('standard', std)}
                            data-testid={`delete-std-${std.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit' : 'Add'} {dialogType === 'productType' ? 'Part Type' : 'Standard'}
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'productType' 
                ? 'Part types appear in the Part Master dropdown (e.g., Bolt, Nut, Washer)'
                : 'Standards appear in the Part Master dropdown (e.g., DIN 933, IS 1364)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={dialogType === 'productType' ? 'e.g., Hex Bolt' : 'e.g., DIN 933'}
                data-testid="dropdown-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                data-testid="dropdown-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-dropdown-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Card */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Usage Guide</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>Part Types</strong> - Define categories like "Hex Bolt", "Nut", "Washer", "Stud", "Pin"</p>
          <p>• <strong>Standards</strong> - Define manufacturing standards like "DIN 933", "DIN 931", "IS 1364", "ASTM A325"</p>
          <p>• Changes here will immediately reflect in the Part Master dropdown menus</p>
          <p>• Deleting an entry will NOT affect existing parts that use that value</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== EMAIL SETTINGS PAGE ==============

export default DropdownManagerPage;
